// src/pages/admin/SubscriptionManagement.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { subscriptionService } from '../../services/subscriptionService';
import { companyService, subscriptionRequestService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/admin/StatusBadge';
import SubscriptionManager from '../../components/admin/SubscriptionManager';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import { showSuccessToast, showErrorToast, showLoadingToast, updateToast } from '../../utils/toastUtils';

const SubscriptionManagement = () => {
  const [searchParams] = useSearchParams();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionRequests, setSubscriptionRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('subscriptions');
  
  // Reply modal state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [replyForm, setReplyForm] = useState({
    status: 'approved',
    response: '',
    executionNotes: ''
  });
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadData();
    
    // Check if we should show requests tab based on URL parameter
    const requestId = searchParams.get('requestId');
    if (requestId) {
      setActiveTab('requests');
    }
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load companies, subscriptions, and subscription requests in parallel
      const [companiesResponse, subscriptionsResponse, requestsResponse] = await Promise.allSettled([
        companyService.getCompanies({ limit: 1000 }),
        subscriptionService.getSubscriptions(),
        subscriptionRequestService.getAllRequests()
      ]);

      let companiesData = [];
      let subscriptionsData = [];

      // Handle companies data
      if (companiesResponse.status === 'fulfilled' && companiesResponse.value.success) {
        companiesData = companiesResponse.value.data || [];
      } else {
        throw new Error('Impossible de charger les données des entreprises');
      }

      // Handle subscriptions data (optional)
      if (subscriptionsResponse.status === 'fulfilled' && subscriptionsResponse.value.success) {
        subscriptionsData = subscriptionsResponse.value.data || [];
      }

      // Normalize company data
      const normalizedCompanies = companiesData.map(company => ({
        ...company,
        id: company.id || company._id
      }));

      // Merge company and subscription data
      const mergedData = normalizedCompanies.map(company => {
        const subscription = subscriptionsData.find(sub => 
          sub.companyId === company.id || sub.companyId === company._id
        );
        
        return {
          ...company,
          subscription: subscription || {
            status: 'inactive',
            startDate: null,
            endDate: null,
            daysRemaining: 0,
            paymentStatus: 'pending'
          }
        };
      });

      setCompanies(normalizedCompanies);
      setSubscriptions(mergedData);

      // Handle subscription requests data
      if (requestsResponse.status === 'fulfilled' && requestsResponse.value.success) {
        setSubscriptionRequests(requestsResponse.value.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = (companyId) => {
    setSelectedCompanyId(companyId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCompanyId(null);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setStatusFilter(value);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Reply to subscription request
  const handleReplyToRequest = (request) => {
    setSelectedRequest(request);
    setReplyForm({
      status: 'approved',
      response: '',
      executionNotes: ''
    });
    setShowReplyModal(true);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyForm.response.trim()) {
      showErrorToast('La réponse est requise');
      return;
    }

    const loadingToastId = showLoadingToast('Envoi de la réponse...');
    setIsSubmittingReply(true);

    try {
      const response = await subscriptionRequestService.replyToRequest(selectedRequest._id, {
        status: replyForm.status,
        adminResponse: replyForm.response,
        executionNotes: replyForm.executionNotes,
        executed: replyForm.status === 'approved'
      });

      if (response.success) {
        updateToast(loadingToastId, 'Réponse envoyée avec succès', 'success');
        setShowReplyModal(false);
        setSelectedRequest(null);
        await loadData(); // Reload data
        
        // Trigger a custom event to refresh notifications globally
        window.dispatchEvent(new CustomEvent('refreshNotifications'));
      } else {
        throw new Error(response.message || 'Erreur lors de l\'envoi de la réponse');
      }
    } catch (error) {
      console.error('Error replying to request:', error);
      updateToast(loadingToastId, error.message || 'Erreur lors de l\'envoi de la réponse', 'error');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const getRequestStatusBadge = (status) => {
    const statusConfig = {
      pending: { type: 'warning', text: 'En attente' },
      approved: { type: 'success', text: 'Approuvée' },
      rejected: { type: 'danger', text: 'Rejetée' }
    };
    return statusConfig[status] || { type: 'secondary', text: status };
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      urgent: { type: 'danger', text: 'Urgent' },
      high: { type: 'warning', text: 'Élevée' },
      medium: { type: 'info', text: 'Moyenne' },
      low: { type: 'secondary', text: 'Faible' }
    };
    return priorityConfig[priority] || { type: 'secondary', text: priority };
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getSubscriptionStatusType = (subscription) => {
    if (!subscription || subscription.status !== 'active') return 'warning';
    const daysRemaining = getDaysRemaining(subscription.endDate);
    if (daysRemaining <= 3) return 'danger';
    if (daysRemaining <= 7) return 'warning';
    return 'success';
  };

  // Filter and sort data
  const filteredSubscriptions = subscriptions
    .filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            (item.domain && item.domain.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || item.subscription.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      let aValue = sortConfig.key.includes('subscription.') 
        ? a.subscription[sortConfig.key.split('.')[1]]
        : a[sortConfig.key];
      let bValue = sortConfig.key.includes('subscription.') 
        ? b.subscription[sortConfig.key.split('.')[1]]
        : b[sortConfig.key];
      
      if (sortConfig.key === 'createdAt' || sortConfig.key === 'subscription.endDate') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });

  // Calculate metrics
  const metrics = {
    active: subscriptions.filter(item => item.subscription.status === 'active').length,
    expired: subscriptions.filter(item => item.subscription.status === 'expired').length,
    inactive: subscriptions.filter(item => item.subscription.status === 'inactive').length,
    expiring: subscriptions.filter(item => {
      const daysRemaining = getDaysRemaining(item.subscription.endDate);
      return item.subscription.status === 'active' && daysRemaining <= 7;
    }).length
  };

  // Table columns
  const columns = [
    {
      key: 'name',
      label: 'Entreprise',
      sortable: true,
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900 text-sm">{value}</div>
          <div className="text-gray-500 text-xs">{item.domain || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'subscription.status',
      label: 'Statut d\'abonnement',
      sortable: true,
      render: (_, item) => {
        const subscription = item.subscription;
        const daysRemaining = getDaysRemaining(subscription.endDate);
        
        let statusText = 'Inactif';
        if (subscription.status === 'active') {
          statusText = `Actif (${daysRemaining}j)`;
        } else if (subscription.status === 'expired') {
          statusText = 'Expiré';
        }
        
        return (
          <StatusBadge 
            status={statusText} 
            type={getSubscriptionStatusType(subscription)} 
            size="sm" 
          />
        );
      }
    },
    {
      key: 'subscription.endDate',
      label: 'Date d\'expiration',
      sortable: true,
      render: (_, item) => (
        <span className="text-gray-500 text-sm">
          {item.subscription.endDate 
            ? new Date(item.subscription.endDate).toLocaleDateString('fr-FR')
            : 'N/A'
          }
        </span>
      )
    },
    {
      key: 'userCount',
      label: 'Utilisateurs',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || 0}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, item) => (
        <button
          onClick={() => handleManageSubscription(item.id || item._id)}
          className="text-xs text-blue-600 hover:text-blue-900 font-medium px-3 py-1 rounded hover:bg-blue-50 transition-colors"
        >
          Gérer
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Erreur</p>
          <p>{error}</p>
          <button 
            onClick={loadData}
            className="mt-2 bg-red-100 px-3 py-1 rounded-md hover:bg-red-200 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Abonnements</h1>
        <div className="text-sm text-gray-500">
          {activeTab === 'subscriptions' 
            ? `Total: ${subscriptions.length} entreprises`
            : `${subscriptionRequests.length} demandes`
          }
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'subscriptions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Abonnements
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                {subscriptions.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Demandes d'Abonnement
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                {subscriptionRequests.filter(req => req.status === 'pending').length}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'subscriptions' ? (
        <>
          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Abonnements Actifs</p>
                  <p className="text-2xl font-semibold text-gray-900">{metrics.active}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Expirent Bientôt</p>
                  <p className="text-2xl font-semibold text-gray-900">{metrics.expiring}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Expirés</p>
                  <p className="text-2xl font-semibold text-gray-900">{metrics.expired}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Inactifs</p>
                  <p className="text-2xl font-semibold text-gray-900">{metrics.inactive}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and filters */}
          <SearchFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filters={[
              {
                key: 'status',
                label: 'Filtrer par statut',
                type: 'select',
                value: statusFilter,
                options: [
                  { value: 'all', label: 'Tous les statuts' },
                  { value: 'active', label: 'Actif' },
                  { value: 'expired', label: 'Expiré' },
                  { value: 'inactive', label: 'Inactif' }
                ]
              }
            ]}
            onFilterChange={handleFilterChange}
            onResetFilters={resetFilters}
            placeholder="Rechercher par nom d'entreprise ou domaine..."
          />

          {/* Data table */}
          <DataTable
            data={filteredSubscriptions}
            columns={columns}
            isLoading={loading}
            onSort={handleSort}
            sortConfig={sortConfig}
            emptyMessage="Aucun abonnement trouvé."
            searchQuery={searchTerm}
            filters={{ status: statusFilter }}
          />

          {/* Results summary */}
          {filteredSubscriptions.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg text-sm text-gray-600">
              Affichage de {filteredSubscriptions.length} abonnement(s) sur {subscriptions.length} au total
            </div>
          )}
        </>
      ) : (
        <>
          {/* Subscription Requests */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Demandes d'Abonnement</h2>
              
              {subscriptionRequests.length > 0 ? (
                <div className="space-y-4">
                  {subscriptionRequests.map((request) => (
                    <div key={request._id} className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <StatusBadge 
                            status={getRequestStatusBadge(request.status).text}
                            type={getRequestStatusBadge(request.status).type}
                            size="sm"
                          />
                          <StatusBadge 
                            status={getPriorityBadge(request.priority).text}
                            type={getPriorityBadge(request.priority).type}
                            size="sm"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                          </span>
                          {request.status === 'pending' && (
                            <button
                              onClick={() => handleReplyToRequest(request)}
                              className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                            >
                              Répondre
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Entreprise</p>
                          <p className="text-sm text-gray-900">{request.companyName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Type</p>
                          <p className="text-sm text-gray-900 capitalize">{request.requestType}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Durée demandée</p>
                          <p className="text-sm text-gray-900">
                            {request.requestedDuration} {request.durationType}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Demandé par</p>
                          <p className="text-sm text-gray-900">{request.requestedByName}</p>
                        </div>
                      </div>
                      
                      {request.message && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-500">Message</p>
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">{request.message}</p>
                        </div>
                      )}
                      
                      {request.adminResponse && (
                        <div className={`rounded-lg p-4 ${
                          request.status === 'approved' ? 'bg-green-50 border border-green-200' :
                          request.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-full ${
                              request.status === 'approved' ? 'bg-green-100' :
                              request.status === 'rejected' ? 'bg-red-100' :
                              'bg-gray-100'
                            }`}>
                              {request.status === 'approved' ? (
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : request.status === 'rejected' ? (
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium mb-2 ${
                                request.status === 'approved' ? 'text-green-800' :
                                request.status === 'rejected' ? 'text-red-800' :
                                'text-gray-800'
                              }`}>
                                Réponse de l'administrateur
                              </p>
                              <p className={`text-sm ${
                                request.status === 'approved' ? 'text-green-700' :
                                request.status === 'rejected' ? 'text-red-700' :
                                'text-gray-700'
                              }`}>
                                {request.adminResponse}
                              </p>
                              {request.reviewedAt && (
                                <p className="text-xs text-gray-500 mt-2">
                                  Révisé le {new Date(request.reviewedAt).toLocaleDateString('fr-FR')} par {request.reviewedByName}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune demande d'abonnement</h3>
                  <p className="mt-1 text-sm text-gray-500">Aucune demande n'a été soumise pour le moment.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Répondre à la demande d'abonnement
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedRequest.companyName} - {selectedRequest.requestType}
                  </p>
                </div>
                <button
                  onClick={() => setShowReplyModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Request Details */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Durée demandée:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedRequest.requestedDuration} {selectedRequest.durationType}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Priorité:</span>
                    <span className="ml-2 text-gray-900">{selectedRequest.priority}</span>
                  </div>
                </div>
                {selectedRequest.message && (
                  <div className="mt-3">
                    <p className="font-medium text-gray-500 text-sm">Message:</p>
                    <p className="text-sm text-gray-700 mt-1">{selectedRequest.message}</p>
                  </div>
                )}
              </div>
              
              <form onSubmit={handleSubmitReply} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Décision *
                  </label>
                  <select
                    value={replyForm.status}
                    onChange={(e) => setReplyForm({...replyForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  >
                    <option value="approved">Approuver</option>
                    <option value="rejected">Rejeter</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Réponse *
                  </label>
                  <textarea
                    value={replyForm.response}
                    onChange={(e) => setReplyForm({...replyForm, response: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder={replyForm.status === 'approved' 
                      ? "Expliquez les détails de l'approbation..." 
                      : "Expliquez les raisons du rejet..."}
                    required
                  />
                </div>

                {replyForm.status === 'approved' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes d'exécution
                    </label>
                    <textarea
                      value={replyForm.executionNotes}
                      onChange={(e) => setReplyForm({...replyForm, executionNotes: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      placeholder="Notes sur l'application de l'extension (optionnel)..."
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowReplyModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingReply}
                    className={`px-6 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 transition-colors flex items-center ${
                      replyForm.status === 'approved'
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubmittingReply ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Envoi...
                      </>
                    ) : (
                      <>
                        {replyForm.status === 'approved' ? (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approuver
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Rejeter
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Subscription manager modal */}
      {showModal && selectedCompanyId && (
        <SubscriptionManager
          companyId={selectedCompanyId}
          onClose={handleCloseModal}
          onUpdate={loadData}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;